import React, { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { 
    TextField, 
    Button, 
    Paper, 
    Typography, 
    Grid,
    Snackbar,
    Alert,
    FormControlLabel,
    Switch,
    Collapse,
    ToggleButton,
    ToggleButtonGroup,
    Box,
    Tooltip,
    IconButton
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router-dom';
import { useBoatInfo } from '../../contexts/BoatContext';

// Separate required and optional measurements
interface RequiredMeasurements {
    name: string;
    model: string;
    loa: string;
    p: string;
    e: string;
    i: string;
    j: string;
}

interface OptionalMeasurements {
    bas: string;
    py: string;
    ii: string;
    ji: string;
    isp: string;
    jsp: string;
}

// Combined type for all form data
interface FormData extends RequiredMeasurements {
    bas?: string;
    py?: string;
    ii?: string;
    ji?: string;
    isp?: string;
    jsp?: string;
}

type RequiredFields = keyof RequiredMeasurements;
type OptionalFields = keyof OptionalMeasurements;
type MeasurementField = RequiredFields | OptionalFields;

// Initial form state
const initialFormData: FormData = {
    name: '',
    model: '',
    loa: '',
    p: '',
    e: '',
    i: '',
    j: ''
};

const measurementTooltips: Record<RequiredFields | OptionalFields, string> = {
    loa: "Length Overall - The maximum length of the boat",
    p: "P Measurement - The distance from the main boom to the top of the mast",
    e: "E Measurement - The horizontal length of the main sail foot",
    i: "I Measurement - The height of the foretriangle",
    j: "J Measurement - The base length of the foretriangle",
    bas: "Boom Above Sheer - Height of boom above sheer line",
    py: "P Yankee - Luff length for yankee headsail",
    ii: "Inner I - Height of inner forestay",
    ji: "Inner J - Base of inner forestay triangle",
    isp: "ISP - Height of spinnaker halyard",
    jsp: "JSP - Spinnaker pole length",
    name: '',
    model: ''
};

export const BoatInfoForm = () => {
    const navigate = useNavigate();
    const { boatInfo, setBoatInfo, updateUnits } = useBoatInfo();
    const [showSuccess, setShowSuccess] = useState(false);
    const [showAdditional, setShowAdditional] = useState(false);
    const [units, setUnits] = useState<'metric' | 'imperial'>(() => {
        return boatInfo?.units || 'imperial';
    });
    const [formData, setFormData] = useState<FormData>(() => {
        if (boatInfo) {
            return {
                name: boatInfo.name || '',
                model: boatInfo.model || '',
                loa: boatInfo.loa?.toString() || '',
                p: boatInfo.p?.toString() || '',
                e: boatInfo.e?.toString() || '',
                i: boatInfo.i?.toString() || '',
                j: boatInfo.j?.toString() || '',
                // Include additional measurements
                bas: boatInfo.bas?.toString() || '',
                py: boatInfo.py?.toString() || '',
                ii: boatInfo.ii?.toString() || '',
                ji: boatInfo.ji?.toString() || '',
                isp: boatInfo.isp?.toString() || '',
                jsp: boatInfo.jsp?.toString() || '',
            };
        }
        return initialFormData;
    });

    // Sync units when boatInfo changes
    useEffect(() => {
        if (boatInfo?.units && boatInfo.units !== units) {
            setUnits(boatInfo.units);
        }
    }, [boatInfo?.units]);

    const handleUnitsChange = (_event: React.MouseEvent<HTMLElement>, newUnits: 'metric' | 'imperial') => {
        if (newUnits !== null) {
            setUnits(newUnits);
            
            // If we have boat info, update it globally
            if (boatInfo) {
                updateUnits(newUnits);
            }
            
            // Convert form data with proper rounding
            setFormData(prev => {
                const newData = { ...prev };
                // Convert all numeric fields if they have values
                Object.entries(prev).forEach(([key, value]) => {
                    if (key !== 'name' && key !== 'model' && value !== '') {
                        const numValue = parseFloat(value);
                        let convertedValue: number;
                        
                        if (units === 'metric' && newUnits === 'imperial') {
                            // meters to feet - 2 decimal places
                            convertedValue = numValue * 3.28084;
                            newData[key as keyof FormData] = (Math.round(convertedValue * 100) / 100).toString();
                        } else if (units === 'imperial' && newUnits === 'metric') {
                            // feet to meters - 3 decimal places
                            convertedValue = numValue * 0.3048;
                            newData[key as keyof FormData] = (Math.round(convertedValue * 1000) / 1000).toString();
                        }
                    }
                });
                return newData;
            });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // For numeric fields, ensure valid number format
        if (name !== 'name' && name !== 'model') {
            if (value !== '') {
                const numValue = parseFloat(value);
                if (isNaN(numValue)) return;
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Removed unused handleNumericChange function

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        console.log('Starting form submission...');
        // Convert string values to numbers for submission
        const processedData = Object.entries(formData).reduce((acc, [key, value]) => {
            if (key === 'name' || key === 'model') {
                acc[key] = value;
            } else if (value !== '') {
                acc[key] = parseFloat(value);
            }
            return acc;
        }, {} as Record<string, string | number>);
        
        // Prepare boat info for context
        const boatInfoForContext = {
            name: formData.name,
            model: formData.model,
            loa: parseFloat(formData.loa),
            units: units,
            p: formData.p ? parseFloat(formData.p) : undefined,
            e: formData.e ? parseFloat(formData.e) : undefined,
            i: formData.i ? parseFloat(formData.i) : undefined,
            j: formData.j ? parseFloat(formData.j) : undefined,
            // Include additional measurements
            bas: formData.bas ? parseFloat(formData.bas) : undefined,
            py: formData.py ? parseFloat(formData.py) : undefined,
            ii: formData.ii ? parseFloat(formData.ii) : undefined,
            ji: formData.ji ? parseFloat(formData.ji) : undefined,
            isp: formData.isp ? parseFloat(formData.isp) : undefined,
            jsp: formData.jsp ? parseFloat(formData.jsp) : undefined
        };

        // Set boat info in context (this will save to session storage)
        setBoatInfo(boatInfoForContext);

        // Debug logging
        console.log('Boat Info Submitted:', boatInfoForContext);
        setShowSuccess(true);
        
        setTimeout(() => {
            navigate('/pricing');
        }, 1500);
    };

    const renderMeasurementField = (name: RequiredFields | OptionalFields, label: string) => (
        <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ position: 'relative', flexGrow: 1, mr: 1 }}>
                    <TextField
                        fullWidth
                        label={label}
                        name={name}
                        type="number"
                        inputProps={{ 
                            step: units === 'metric' ? "0.001" : "0.01",
                            min: "0"
                        }}
                        value={formData[name as keyof FormData] || ''}
                        onChange={handleChange}
                        required={!name.match(/^(bas|py|ii|ji|isp|jsp)$/)}
                        InputProps={{
                            endAdornment: (
                                <Box sx={{ 
                                    position: 'absolute', 
                                    right: 8, 
                                    top: '50%', 
                                    transform: 'translateY(-50%)',
                                    backgroundColor: '#e9ecef',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '0.8rem',
                                    color: '#6c757d',
                                    border: '1px solid #ced4da',
                                    minWidth: '30px',
                                    textAlign: 'center',
                                    pointerEvents: 'none'
                                }}>
                                    {units === 'metric' ? 'm' : 'ft'}
                                </Box>
                            )
                        }}
                        sx={{ 
                            '& .MuiOutlinedInput-root': {
                                paddingRight: '60px'
                            }
                        }}
                    />
                </Box>
                <Tooltip title={measurementTooltips[name as RequiredFields | OptionalFields]}>
                    <IconButton size="small">
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Grid>
    );

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Boat Information</Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    {/* Basic Info */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Boat Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Boat Model"
                            name="model"
                            value={formData.model}
                            onChange={handleChange}
                            required
                        />
                    </Grid>

                    {/* Units Toggle */}
                    <Grid item xs={12}>
                        <ToggleButtonGroup
                            value={units}
                            exclusive
                            onChange={handleUnitsChange}
                            aria-label="measurement units"
                            size="small"
                        >
                            <ToggleButton value="metric">Metric (m)</ToggleButton>
                            <ToggleButton value="imperial">Imperial (ft)</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    {/* Primary Measurements */}
                    {renderMeasurementField('loa', 'LOA')}
                    {renderMeasurementField('p', 'P')}
                    {renderMeasurementField('e', 'E')}
                    {renderMeasurementField('i', 'I')}
                    {renderMeasurementField('j', 'J')}

                    {/* Additional Measurements Toggle */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showAdditional}
                                    onChange={(e) => setShowAdditional(e.target.checked)}
                                />
                            }
                            label="Show Additional Measurements"
                        />
                    </Grid>

                    {/* Additional Measurements */}
                    <Grid item xs={12}>
                        <Collapse in={showAdditional}>
                            <Grid container spacing={2}>
                                {renderMeasurementField('bas', 'BAS')}
                                {renderMeasurementField('py', 'PY')}
                                {renderMeasurementField('ii', 'Ii')}
                                {renderMeasurementField('ji', 'Ji')}
                                {renderMeasurementField('isp', 'ISP')}
                                {renderMeasurementField('jsp', 'JSP')}
                            </Grid>
                        </Collapse>
                    </Grid>

                    <Grid item xs={12}>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            color="primary"
                            size="large"
                            sx={{ mt: 2 }}
                        >
                            Save Boat Information
                        </Button>
                    </Grid>
                </Grid>
            </form>

            <Snackbar
                open={showSuccess}
                autoHideDuration={1500}
                onClose={() => setShowSuccess(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="success" sx={{ width: '100%' }}>
                    Boat information saved successfully!
                </Alert>
            </Snackbar>
        </Paper>
    );
};